import { Button, CircularProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";
import ClientService from "../../services/clients/ClientService";
import CommonService from "../../services/common/commonService";
import "../../styles/FingertipMetrics.css";
import { useContext, useEffect, useMemo, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import Ask7GIconPurple from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";
import React from "react";
import FingertipMetricsView from "./FingertipMetricView";
import Ask7GIcon from "../../assets/navigations/Ask7g-icon.svg";
import { GlobalContext } from "../../context/GlobalContext";
import InteractionsTimeline from "../Common/InteractionsTimeline";
import { fingertipMetricViewStyles as styles } from "./FingertipMetricView.styles";
import { getPresetDateRange, mapToTimelineItems, TimelineItem } from "../../utils/utils";

const LOADING_MESSAGES = [
  "Crunching data magic…",
  "Analyzing your meeting…",
  "Tidying up your summary…",
  "Piecing insights together…",
  "Optimizing for clarity…",
  "Composing the essentials…",
  "Making it perfect…",
  "Scanning for highlights…",
  "Almost ready…",
  "Final touch-ups…",
];

const ClientFingerTipMetrics: React.FC<{
  clientOrgId: string | undefined;
  subOrgId: string;
  isSlideOverOpen?: boolean;
  clientName?: string | undefined;
  tooltipNavigationState?: Record<string, any>;
}> = ({ clientOrgId, subOrgId, isSlideOverOpen, clientName, tooltipNavigationState,}) => {
  const [ data, setData ] = useState<any | null>(null);
  const [dynamicMessage, setDynamicMessage] = React.useState("");
  const { impersonationDetails } = useContext(GlobalContext);
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  const { showSnackbar } = useDisplaySnackbar();
  const { 
    isLoading: isGenerateLoading,
    isFetching: isGenerateFetching,
    isSuccess: isGenerateSuccess,
    isError: isGenerateError,
    error: generateError,
    data: generateData,
    refetch: fetchFingerTipMetrics,
  } = ClientService.useGetGenerateFingertipMetrics(
    {
      orgId: clientOrgId,
      subOrgId: subOrgId,
    },false
  )

  useEffect(() => {
    if (isGenerateError){
      showSnackbar("error", generateError || "Failed to generate the fingertip metrics");
    }
  },[isGenerateSuccess, isGenerateError, generateError, generateData]);

  const {
    isLoading: isMetricsLoading,
    isFetching: isMetricsFetching,
    isSuccess: isMetricsSuccess,
    isError: isMetricsError,
    error: metricsError,
    data: metricsData,
    refetch: refetchMetrics,
  } = ClientService.useGetFingerTipMetrics(
    {
      orgId: clientOrgId,
      subOrgId: subOrgId,
    },
    !!clientOrgId
  )

  useEffect(() => {
    if(isMetricsSuccess && metricsData) {
      setData(metricsData?.data)
    }
    else if (isMetricsError){
      showSnackbar("error", metricsError || "Failed to generate the fingertip metrics");
    }
  },[isMetricsSuccess, isMetricsError, metricsError, metricsData]);

  const handleRegenerate = async () => {
    try {
      await fetchFingerTipMetrics();   // generate
      await refetchMetrics();          // re-fetch metrics
    } catch {
      showSnackbar("error", generateError || "Failed to regenerate metrics");
    }
  };

  const isCompleted = data && data?.ai_analysis_status === "completed";
  const isInProgress = data && data?.ai_analysis_status === "InProgress";
  const isGenerating = isGenerateLoading || isGenerateFetching;
  const isGeneratingOrLoading = isGenerating || isMetricsLoading || isMetricsFetching || isInProgress;
  const showEmptyState = !isCompleted && !isGeneratingOrLoading;
  const showRegenerate = data?.regeneratable === true;

  const initialThreeMonthRange = getPresetDateRange("last3MonthsFull");
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;

  const {
    data: interactionsData,
    isError: isInteractionsError,
    error: interactionsError,
  } = CommonService.useGetFingertipTrendLineInteractions(
    {
      startTime: initialThreeMonthRange.start_time,
      endTime: initialThreeMonthRange.end_time,
      subOrgId: subOrgId,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    !!subOrgId,
  );

  useEffect(() => {
    if (isInteractionsError) {
      showSnackbar("error", interactionsError?.message || "Failed to load timeline data.");
    }
  }, [interactionsError, isInteractionsError]);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const orgRow = (interactionsData?.data || [])[0];
    if (!orgRow) return [];
    return mapToTimelineItems(orgRow.interactions, orgRow.emails);
  }, [interactionsData]);

  const monthlyScore = useMemo<Record<string, string> | null>(() => {
    const orgRow = (interactionsData?.data || [])[0];
    return orgRow?.monthlyScore && typeof orgRow.monthlyScore === "object"
      ? orgRow.monthlyScore
      : null;
  }, [interactionsData]);

  useEffect(() => {
    if (isGeneratingOrLoading) {
      const msg = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
      setDynamicMessage(msg);
    }
  }, [isGeneratingOrLoading]);

  useEffect(() => {
    if (!data || data.ai_analysis_status !== "InProgress") return;
    const intervalId = setInterval(() => {
      refetchMetrics();
    }, 5000); // 5 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [data?.ai_analysis_status, refetchMetrics]);

  if (isCompleted && data?.value && !isInProgress) {
    return (
      <FingertipMetricsView 
        value={data.value} 
        regenerate={showRegenerate} 
        onRegenerate={handleRegenerate}
        isGenerateLoading={isGenerateLoading && isGenerateFetching}
        isSlideOverOpen={isSlideOverOpen}
        tooltipNavigationState={tooltipNavigationState}
        clientName={clientName}
        timelineItems={timelineItems}
        monthlyScore={monthlyScore}
        dateRange={initialThreeMonthRange}
      />
    );
  }
  
  return (
    <Box sx={{ padding: "8px", overflowY: "auto", maxHeight: "calc(100vh - 235px)" }}>
      <Box
        sx={{
          ...styles.summaryCard,
          ...styles.trendsCard,
          marginBottom: "16px",
        }}
      >
        <InteractionsTimeline
          items={timelineItems}
          startDate={initialThreeMonthRange.start_time}
          endDate={initialThreeMonthRange.end_time}
          mode="both"
          monthlyScore={monthlyScore}
          navigationState={tooltipNavigationState}
          isSlideOverOpen={isSlideOverOpen}
        />
      </Box>

      {isGeneratingOrLoading && (
        <Box sx={{ height: "400px" }}>
          <Box className="prep-notes-loading-container">
            <svg width="0" height="0" style={{ position: "absolute" }}>
              <defs>
                <linearGradient
                  id="ask7gGradient"
                  x1="2.52"
                  y1="32.34"
                  x2="33.6"
                  y2="8.4"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
                  <stop offset="0.995265" stopColor="#7F018B" />
                </linearGradient>
              </defs>
            </svg>

            <Box className="prep-notes-progress-box">
              <CircularProgress
                size={50}
                thickness={3}
                sx={{
                  "& .MuiCircularProgress-circle": {
                    stroke: "url(#ask7gGradient)",
                  },
                }}
              />
              <Box className="prep-notes-progress-icon-box">
                <img
                  src={Ask7GIconPurple}
                  alt="Ask7G"
                  className="prep-notes-progress-icon"
                />
              </Box>
            </Box>

            <Typography
              className="prep-notes-generating-text"
              sx={{ marginTop: "16px" }}
            >
              {dynamicMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {showEmptyState && (
        <Box className="main-container">
          <Box
            sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: showRegenerate ? "450px" : undefined,
            opacity: !showRegenerate || isImpersonating ? 0.4 : 1,
            transition: "opacity 0.3s ease",
          }}
          >
            {showRegenerate ? (
              <>
                <Typography className="generate-text">
                  Quickly generate AI-powered Fingertip metrics summarizing agenda,
                  participants, and key details for your upcoming interaction.
                </Typography>
                <Button
                  variant="contained"
                  className="fm-generate-btn"
                  onClick={handleRegenerate}
                  disabled={!showRegenerate || isImpersonating || isGeneratingOrLoading}
                  sx={{
                    opacity: !showRegenerate || isImpersonating ? 0.4 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <img
                    src={Ask7GIcon}
                    alt="Ask7G"
                    className="fm-generate-btn-icon"
                  />
                  Generate Fingertip Metrics
                </Button>
              </>
            ) : (
              <Typography style={{ fontSize: "16px", fontWeight: 500, color: "#6B6F76" }}>
                There are no recordings available to generate a fingertip metrics.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ClientFingerTipMetrics;
